import { useState } from 'react'
import Modal from '@/components/common/Modal'
import { Upload, FileSpreadsheet, Archive, AlertCircle, CheckCircle2 } from 'lucide-react'
import toast from 'react-hot-toast'

export default function BulkImportModal({ open, onClose, title, onImport, downloadTemplateUrl, onDownloadTemplate }) {
    const [excelFile, setExcelFile] = useState(null)
    const [zipFile, setZipFile] = useState(null)
    const [loading, setLoading] = useState(false)
    const [result, setResult] = useState(null)

    const handleClose = () => {
        if (!loading) {
            setExcelFile(null)
            setZipFile(null)
            setResult(null)
            onClose()
        }
    }

    const handleExcelChange = (e) => {
        const selected = e.target.files[0]
        if (selected && (selected.name.endsWith('.xlsx') || selected.name.endsWith('.xls'))) {
            setExcelFile(selected)
            setResult(null)
        } else {
            toast.error('Допустимы только файлы Excel (.xlsx, .xls)')
        }
    }

    const handleZipChange = (e) => {
        const selected = e.target.files[0]
        if (selected && selected.name.endsWith('.zip')) {
            setZipFile(selected)
            setResult(null)
        } else {
            toast.error('Допустимы только файлы ZIP')
        }
    }

    const handleSubmit = async () => {
        if (!excelFile && !zipFile) {
            toast.error('Выберите хотя бы один файл')
            return
        }
        setLoading(true)
        setResult(null)
        try {
            const resp = await onImport(excelFile, zipFile)
            // Expecting resp.data to have { created: int, updated: int, errors: array }
            setResult({
                success: true,
                created: resp.data.created || 0,
                updated: resp.data.updated || 0,
                errors: resp.data.errors || []
            })
            if (resp.data.errors?.length === 0) {
                toast.success(`Успешно: создано ${resp.data.created}, обновлено ${resp.data.updated}`)
            } else {
                toast('Импорт завершен с ошибками', { icon: '⚠️' })
            }
        } catch (err) {
            console.error('Import error:', err)
            setResult({
                success: false,
                message: err.response?.data?.error || 'Ошибка загрузки файлов'
            })
            toast.error('Ошибка импорта')
        } finally {
            setLoading(false)
        }
    }

    return (
        <Modal
            open={open}
            onClose={handleClose}
            title={title || "Массовый импорт (Excel + ZIP)"}
            size="md"
            footer={
                result?.success && result?.errors?.length === 0 ? (
                    <button className="btn-primary" onClick={handleClose}>Готово</button>
                ) : (
                    <>
                        <button className="btn-secondary" onClick={handleClose} disabled={loading}>
                            Отмена
                        </button>
                        <button
                            className="btn-primary"
                            onClick={handleSubmit}
                            disabled={(!excelFile && !zipFile) || loading || result?.success}
                        >
                            {loading ? 'Загрузка...' : 'Импортировать'}
                        </button>
                    </>
                )
            }
        >
            <div className="space-y-4">
                <div className="flex items-center justify-between gap-4">
                    <p className="text-sm text-gray-500 text-left">
                        Вы можете загрузить Excel список вместе с ZIP архивом.
                    </p>
                    {(downloadTemplateUrl || onDownloadTemplate) && (
                        onDownloadTemplate ? (
                            <button
                                onClick={onDownloadTemplate}
                                className="shrink-0 text-sm text-primary-600 hover:underline flex items-center gap-1 font-medium ml-auto"
                            >
                                <FileSpreadsheet size={16} /> Шаблон Excel
                            </button>
                        ) : (
                            <a href={downloadTemplateUrl} download className="shrink-0 text-sm text-primary-600 hover:underline flex items-center gap-1 font-medium">
                                <FileSpreadsheet size={16} /> Шаблон Excel
                            </a>
                        )
                    )}
                </div>

                {/* Upload Section */}
                {!result?.success && (
                    <div className="flex flex-col gap-3">
                        <div className="flex items-center gap-4 p-4 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                                <FileSpreadsheet size={20} />
                            </div>
                            <div className="flex-1 min-w-0 text-left">
                                <p className="text-sm font-medium text-gray-700 truncate">
                                    {excelFile ? excelFile.name : "Тут список (.xlsx)"}
                                </p>
                                <label className="text-xs text-primary-600 hover:underline cursor-pointer">
                                    Выбрать Excel (необязательно)
                                    <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleExcelChange} />
                                </label>
                            </div>
                            {excelFile && <CheckCircle2 size={18} className="text-green-500" />}
                        </div>

                        <div className="flex items-center gap-4 p-4 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                            <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center text-orange-600">
                                <Archive size={20} />
                            </div>
                            <div className="flex-1 min-w-0 text-left">
                                <p className="text-sm font-medium text-gray-700 truncate">
                                    {zipFile ? zipFile.name : "Тут архив с фото (.zip)"}
                                </p>
                                <label className="text-xs text-primary-600 hover:underline cursor-pointer">
                                    Выбрать ZIP (необязательно)
                                    <input type="file" accept=".zip" className="hidden" onChange={handleZipChange} />
                                </label>
                            </div>
                            {zipFile && <CheckCircle2 size={18} className="text-green-500" />}
                        </div>
                    </div>
                )}

                {/* Result State */}
                {result && (
                    <div className={`p-4 rounded-xl ${result.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                        <div className="flex items-center gap-2 mb-2 font-medium">
                            {result.success ? (
                                <CheckCircle2 className="text-green-600" size={18} />
                            ) : (
                                <AlertCircle className="text-red-600" size={18} />
                            )}
                            <span className={result.success ? 'text-green-800' : 'text-red-800'}>
                                {result.success ? 'Результат импорта' : 'Ошибка импорта'}
                            </span>
                        </div>

                        {result.success ? (
                            <div className="space-y-2 text-sm text-green-700 text-left">
                                <p>Создано: <strong>{result.created}</strong></p>
                                <p>Обновлено: <strong>{result.updated}</strong></p>
                                {result.errors?.length > 0 && (
                                    <div className="mt-3">
                                        <p className="font-semibold text-red-600 mb-1">Ошибки ({result.errors.length}):</p>
                                        <ul className="text-xs text-red-500 list-disc list-inside space-y-1 max-h-40 overflow-y-auto">
                                            {result.errors.map((err, i) => (
                                                <li key={i}>{err}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <p className="text-sm text-red-600 text-left">{result.message}</p>
                        )}
                    </div>
                )}
            </div>
        </Modal>
    )
}
